import React, { useState } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import "../../../assets/css/admin/AITutor.css";
import AdminLayout from "../../../layouts/AdminLayout/AdminLayout";

const AITutor = () => {
  const [selectedTab, setSelectedTab] = useState("overview");
  const [isTraining, setIsTraining] = useState(false);

  // AI Performance Metrics
  const performanceData = [
    { day: "Mon", conversations: 245, responses: 1234, accuracy: 94 },
    { day: "Tue", conversations: 268, responses: 1456, accuracy: 95 },
    { day: "Wed", conversations: 290, responses: 1589, accuracy: 96 },
    { day: "Thu", conversations: 310, responses: 1678, accuracy: 95 },
    { day: "Fri", conversations: 285, responses: 1523, accuracy: 97 },
    { day: "Sat", conversations: 195, responses: 987, accuracy: 94 },
    { day: "Sun", conversations: 178, responses: 876, accuracy: 93 },
  ];

  // Response Time Distribution
  const responseTimeData = [
    { range: "<1s", count: 5234 },
    { range: "1-2s", count: 3456 },
    { range: "2-3s", count: 1234 },
    { range: "3-5s", count: 567 },
    { range: ">5s", count: 123 },
  ];

  // Topic Distribution
  const topicData = [
    { name: "JavaScript", value: 35, queries: 4234 },
    { name: "React", value: 28, queries: 3389 },
    { name: "Python", value: 20, queries: 2423 },
    { name: "Database", value: 10, queries: 1211 },
    { name: "Other", value: 7, queries: 847 },
  ];

  const COLORS = ["#781c2e", "#922636", "#a63042", "#ba3a4e", "#ce445a"];

  // User Satisfaction Over Time
  const satisfactionData = [
    { week: "Week 1", satisfaction: 88, resolved: 456 },
    { week: "Week 2", satisfaction: 90, resolved: 523 },
    { week: "Week 3", satisfaction: 92, resolved: 589 },
    { week: "Week 4", satisfaction: 94, resolved: 634 },
  ];

  // Key Metrics
  const keyMetrics = [
    {
      title: "Total Conversations",
      value: "12,456",
      change: "+15.3%",
      trend: "up",
      icon: "message",
    },
    {
      title: "Questions Answered",
      value: "34,890",
      change: "+22.1%",
      trend: "up",
      icon: "help",
    },
    {
      title: "Avg Response Time",
      value: "1.2s",
      change: "-8.5%",
      trend: "up",
      icon: "clock",
    },
    {
      title: "Satisfaction Rate",
      value: "94%",
      change: "+3.2%",
      trend: "up",
      icon: "star",
    },
  ];

  // Recent Conversations
  const recentConversations = [
    {
      id: 1,
      user: "Ahmed Khan",
      topic: "React Hooks",
      status: "resolved",
      satisfaction: 5,
      time: "2 mins ago",
    },
    {
      id: 2,
      user: "Sara Ali",
      topic: "Python Lists",
      status: "resolved",
      satisfaction: 4,
      time: "5 mins ago",
    },
    {
      id: 3,
      user: "Hassan Raza",
      topic: "SQL Joins",
      status: "active",
      satisfaction: null,
      time: "10 mins ago",
    },
    {
      id: 4,
      user: "Fatima Malik",
      topic: "CSS Flexbox",
      status: "resolved",
      satisfaction: 5,
      time: "15 mins ago",
    },
  ];

  // AI Model Settings
  const modelSettings = {
    version: "Claude Sonnet 4.5",
    temperature: 0.7,
    maxTokens: 4096,
    topP: 0.9,
    frequencyPenalty: 0.0,
    presencePenalty: 0.0,
  };

  const getIcon = (iconType) => {
    const icons = {
      message: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      ),
      help: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
      ),
      clock: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
      ),
      star: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
        </svg>
      ),
    };
    return icons[iconType];
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <span key={i} className={`star ${i < rating ? "filled" : ""}`}>
        ★
      </span>
    ));
  };

  return (
    <AdminLayout>
      <div className="ai-tutor-container">
        {/* Header */}
        <div className="ai-tutor-header">
          <div>
            <h2>AI Tutor Management</h2>
            <p className="ai-tutor-subtitle">
              Monitor and optimize your AI teaching assistant
            </p>
          </div>
          <div className="header-actions">
            <button className="status-btn active">
              <span className="status-indicator"></span>
              AI Active
            </button>
            <button
              className={`train-btn ${isTraining ? "training" : ""}`}
              onClick={() => setIsTraining(!isTraining)}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"></path>
              </svg>
              {isTraining ? "Training..." : "Retrain Model"}
            </button>
            {/* <button className="def-martins-btn">Def Martins</button> */}
          </div>
        </div>

        {/* Tabs */}
        <div className="ai-tutor-tabs">
          <button
            className={`tab-btn ${selectedTab === "overview" ? "active" : ""}`}
            onClick={() => setSelectedTab("overview")}
          >
            Overview
          </button>
          <button
            className={`tab-btn ${selectedTab === "analytics" ? "active" : ""}`}
            onClick={() => setSelectedTab("analytics")}
          >
            Analytics
          </button>
          <button
            className={`tab-btn ${
              selectedTab === "conversations" ? "active" : ""
            }`}
            onClick={() => setSelectedTab("conversations")}
          >
            Conversations
          </button>
          <button
            className={`tab-btn ${selectedTab === "settings" ? "active" : ""}`}
            onClick={() => setSelectedTab("settings")}
          >
            Settings
          </button>
        </div>

        {/* Overview Tab */}
        {selectedTab === "overview" && (
          <>
            {/* Key Metrics */}
            <div className="metrics-grid">
              {keyMetrics.map((metric, index) => (
                <div key={index} className="metric-card">
                  <div className="metric-icon">{getIcon(metric.icon)}</div>
                  <div className="metric-content">
                    <span className="metric-label">{metric.title}</span>
                    <div className="metric-value-row">
                      <span className="metric-value">{metric.value}</span>
                      <span className={`metric-change ${metric.trend}`}>
                        {metric.trend === "up" ? "↑" : "↓"} {metric.change}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Charts Grid */}
            <div className="charts-grid">
              {/* AI Performance Trend */}
              <div className="chart-card full-width">
                <div className="chart-header">
                  <h3>AI Performance Trend</h3>
                  <div className="chart-legend">
                    <span className="legend-item">
                      <span
                        className="legend-dot"
                        style={{ backgroundColor: "#781c2e" }}
                      ></span>
                      Conversations
                    </span>
                    <span className="legend-item">
                      <span
                        className="legend-dot"
                        style={{ backgroundColor: "#4CAF50" }}
                      ></span>
                      Responses
                    </span>
                    <span className="legend-item">
                      <span
                        className="legend-dot"
                        style={{ backgroundColor: "#FFC107" }}
                      ></span>
                      Accuracy %
                    </span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={performanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="day" stroke="#999" />
                    <YAxis stroke="#999" />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="conversations"
                      stroke="#781c2e"
                      strokeWidth={3}
                      dot={{ r: 5 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="responses"
                      stroke="#4CAF50"
                      strokeWidth={3}
                      dot={{ r: 5 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="accuracy"
                      stroke="#FFC107"
                      strokeWidth={3}
                      dot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Topic Distribution */}
              <div className="chart-card">
                <div className="chart-header">
                  <h3>Most Asked Topics</h3>
                </div>
                <div className="pie-chart-container">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={topicData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) =>
                          `${name} ${(percent * 100).toFixed(0)}%`
                        }
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {topicData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="topic-stats">
                    {topicData.map((topic, index) => (
                      <div key={index} className="topic-stat-item">
                        <div
                          className="topic-stat-color"
                          style={{ backgroundColor: COLORS[index] }}
                        ></div>
                        <div className="topic-stat-info">
                          <span className="topic-stat-name">{topic.name}</span>
                          <span className="topic-stat-queries">
                            {topic.queries} queries
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Response Time */}
              <div className="chart-card">
                <div className="chart-header">
                  <h3>Response Time Distribution</h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={responseTimeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="range" stroke="#999" />
                    <YAxis stroke="#999" />
                    <Tooltip />
                    <Bar dataKey="count" fill="#781c2e" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Recent Conversations */}
            <div className="conversations-section">
              <h3>Recent Conversations</h3>
              <div className="conversations-list">
                {recentConversations.map((conv) => (
                  <div key={conv.id} className="conversation-item">
                    <div className="conversation-avatar">
                      {conv.user.charAt(0)}
                    </div>
                    <div className="conversation-content">
                      <div className="conversation-header">
                        <span className="conversation-user">{conv.user}</span>
                        <span className="conversation-time">{conv.time}</span>
                      </div>
                      <div className="conversation-topic">{conv.topic}</div>
                      <div className="conversation-footer">
                        <span className={`conversation-status ${conv.status}`}>
                          {conv.status}
                        </span>
                        {conv.satisfaction && (
                          <div className="conversation-rating">
                            {renderStars(conv.satisfaction)}
                          </div>
                        )}
                      </div>
                    </div>
                    <button className="view-conversation-btn">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Analytics Tab */}
        {selectedTab === "analytics" && (
          <div className="analytics-tab">
            <div className="charts-grid">
              {/* Satisfaction Trend */}
              <div className="chart-card full-width">
                <div className="chart-header">
                  <h3>User Satisfaction Trend</h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={satisfactionData}>
                    <defs>
                      <linearGradient id="colorSat" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="5%"
                          stopColor="#781c2e"
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor="#781c2e"
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" stroke="#999" />
                    <YAxis stroke="#999" />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="satisfaction"
                      stroke="#781c2e"
                      fillOpacity={1}
                      fill="url(#colorSat)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Performance Metrics */}
              <div className="performance-metrics">
                <div className="performance-card">
                  <div className="performance-icon success">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <div className="performance-content">
                    <h4>High Accuracy</h4>
                    <p>AI maintains 95%+ accuracy on technical questions</p>
                    <span className="performance-stat">95.3% avg accuracy</span>
                  </div>
                </div>

                <div className="performance-card">
                  <div className="performance-icon warning">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                  </div>
                  <div className="performance-content">
                    <h4>Response Delay</h4>
                    <p>3.2% of responses exceed 3 seconds</p>
                    <span className="performance-stat">
                      Optimization recommended
                    </span>
                  </div>
                </div>

                <div className="performance-card">
                  <div className="performance-icon info">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M12 2v6m0 6v6M8 2l1 1m5 0l1-1M8 22l1-1m5 0l1 1"></path>
                    </svg>
                  </div>
                  <div className="performance-content">
                    <h4>Peak Hours</h4>
                    <p>Most activity between 2 PM - 8 PM</p>
                    <span className="performance-stat">
                      Consider scaling resources
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {selectedTab === "settings" && (
          <div className="settings-tab">
            <div className="settings-grid">
              {/* Model Configuration */}
              <div className="settings-card">
                <h3>Model Configuration</h3>
                <div className="settings-form">
                  <div className="form-group">
                    <label>Model Version</label>
                    <select className="form-input">
                      <option>{modelSettings.version}</option>
                      <option>Claude Opus 4</option>
                      <option>Claude Haiku 4.5</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Temperature ({modelSettings.temperature})</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      defaultValue={modelSettings.temperature}
                      className="form-range"
                    />
                    <span className="range-hint">
                      Lower = More focused, Higher = More creative
                    </span>
                  </div>
                  <div className="form-group">
                    <label>Max Tokens</label>
                    <input
                      type="number"
                      defaultValue={modelSettings.maxTokens}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Top P ({modelSettings.topP})</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      defaultValue={modelSettings.topP}
                      className="form-range"
                    />
                  </div>
                </div>
              </div>

              {/* Training Data */}
              <div className="settings-card">
                <h3>Training Data</h3>
                <div className="training-stats">
                  <div className="training-stat-item">
                    <span className="training-label">
                      Knowledge Base Articles
                    </span>
                    <span className="training-value">2,456</span>
                  </div>
                  <div className="training-stat-item">
                    <span className="training-label">Code Examples</span>
                    <span className="training-value">5,892</span>
                  </div>
                  <div className="training-stat-item">
                    <span className="training-label">Q&A Pairs</span>
                    <span className="training-value">12,345</span>
                  </div>
                  <div className="training-stat-item">
                    <span className="training-label">Last Updated</span>
                    <span className="training-value">2 days ago</span>
                  </div>
                </div>
                <button className="upload-btn">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                  Upload Training Data
                </button>
              </div>

              {/* Response Templates */}
              <div className="settings-card full-width">
                <h3>Response Templates</h3>
                <div className="templates-list">
                  <div className="template-item">
                    <div className="template-header">
                      <span className="template-name">Greeting Template</span>
                      <button className="template-edit-btn">Edit</button>
                    </div>
                    <p className="template-preview">
                      Hello! I'm your AI tutor. How can I help you today?
                    </p>
                  </div>
                  <div className="template-item">
                    <div className="template-header">
                      <span className="template-name">Code Explanation</span>
                      <button className="template-edit-btn">Edit</button>
                    </div>
                    <p className="template-preview">
                      Let me break down this code for you step by step...
                    </p>
                  </div>
                  <div className="template-item">
                    <div className="template-header">
                      <span className="template-name">
                        Clarification Request
                      </span>
                      <button className="template-edit-btn">Edit</button>
                    </div>
                    <p className="template-preview">
                      Could you provide more details about your question?
                    </p>
                  </div>
                </div>
                <button className="add-template-btn">+ Add New Template</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AITutor;
